import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { LanguageTarget } from '../api/types';
import { LanguagePicker } from './LanguagePicker';
import { isHttpUrl } from './qrCodeFormParts';

export const MAX_LANGUAGES = 20;

export const STARTING_LANGUAGE = 'en';

const TAG_PATTERN = /^([A-Za-z]{2,3})(?:[-_]([A-Za-z]{2}|[0-9]{3}))?$/;

export function normalizeLanguage(tag: string): string | null {
  const match = TAG_PATTERN.exec(tag.trim());
  if (!match) {
    return null;
  }

  return match[2] ? `${match[1].toLowerCase()}-${match[2].toUpperCase()}` : match[1].toLowerCase();
}

export function startLanguages(targetUrl: string): LanguageTarget[] {
  return [{ language: STARTING_LANGUAGE, targetUrl, isDefault: true }];
}

export function defaultTargetUrl(targets: LanguageTarget[]): string {
  return targets.find((target) => target.isDefault)?.targetUrl ?? '';
}

export function languageTargetsError(
  targets: LanguageTarget[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  const seen = new Set<string>();

  for (const [index, target] of targets.entries()) {
    if (!target.language.trim()) {
      return t('qrCode.languages.missing', { number: index + 1 });
    }

    const tag = normalizeLanguage(target.language);
    if (!tag) {
      return t('qrCode.languages.invalidTag', { tag: target.language.trim() });
    }

    if (seen.has(tag)) {
      return t('qrCode.languages.duplicate', { tag });
    }

    seen.add(tag);

    if (!isHttpUrl(target.targetUrl.trim())) {
      return t('qrCode.languages.invalidUrl', { tag });
    }
  }

  return null;
}

export function normalizedLanguageTargets(targets: LanguageTarget[]): LanguageTarget[] {
  return targets.map((target) => ({
    language: normalizeLanguage(target.language) ?? target.language.trim(),
    targetUrl: target.targetUrl.trim(),
    isDefault: target.isDefault,
  }));
}

export function LanguageTargetsEditor({
  id,
  value,
  onChange,
  disabled = false,
}: {
  id?: string;
  value: LanguageTarget[];
  onChange: (targets: LanguageTarget[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const ids = useId();

  function update(index: number, patch: Partial<LanguageTarget>) {
    onChange(value.map((target, position) => (position === index ? { ...target, ...patch } : target)));
  }

  function makeDefault(index: number) {
    onChange(value.map((target, position) => ({ ...target, isDefault: position === index })));
  }

  function remove(index: number) {
    const rest = value.filter((_, position) => position !== index);
    onChange(rest.some((target) => target.isDefault) || rest.length === 0 ? rest : rest.map((target, position) => ({ ...target, isDefault: position === 0 })));
  }

  return (
    <div className="language-targets">
      <ul className="language-targets__list">
        {value.map((target, index) => (
          <li key={index} className="language-targets__row">
            <div className="language-targets__tag">
              <LanguagePicker
                id={index === 0 ? id : undefined}
                value={target.language}
                taken={value.filter((_, position) => position !== index).map((other) => other.language)}
                label={t('qrCode.languages.language', { number: index + 1 })}
                disabled={disabled}
                onChange={(language) => update(index, { language })}
              />
            </div>
            <input
              type="url"
              className="form-control form-control-sm language-targets__url"
              aria-label={t('qrCode.languages.link', { number: index + 1 })}
              placeholder="https://example.com/fr"
              value={target.targetUrl}
              disabled={disabled}
              onChange={(event) => update(index, { targetUrl: event.target.value })}
            />
            <div className="form-check mb-0 language-targets__default">
              <input
                id={`${ids}-default-${index}`}
                type="radio"
                name={`${ids}-default`}
                className="form-check-input"
                checked={target.isDefault}
                disabled={disabled}
                onChange={() => makeDefault(index)}
              />
              <label className="form-check-label small" htmlFor={`${ids}-default-${index}`}>
                {t('qrCode.languages.default')}
              </label>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              aria-label={t('qrCode.languages.remove', { number: index + 1 })}
              title={t('qrCode.languages.remove', { number: index + 1 })}
              disabled={disabled || value.length === 1}
              onClick={() => remove(index)}
            >
              <i className="pi pi-trash" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        disabled={disabled || value.length >= MAX_LANGUAGES}
        onClick={() => onChange([...value, { language: '', targetUrl: '', isDefault: value.length === 0 }])}
      >
        <i className="pi pi-plus me-1" aria-hidden="true" />
        {t('qrCode.languages.add')}
      </button>
      <span className="hint d-block">{t('qrCode.languages.hint')}</span>
    </div>
  );
}

export function MultiLanguageSwitch({
  checked,
  disabled = false,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className="form-check form-switch mb-0 multi-language-switch">
      <label className="form-check-label" htmlFor={id}>
        {t('qrCode.languages.toggle')}
      </label>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="form-check-input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  );
}
